import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { Text } = Typography;

type HotelProperty = {
  id: number;
  name: string;
};

type Currency = {
  id: number;
  code: string;
  symbol?: string | null;
  name: string;
};

type PaymentMode = {
  id: number;
  name: string;
  mode_type?: string | null;
};

type HotelPayment = {
  id: number;
  property_id: number;
  property_name?: string | null;
  reservation_id?: number | null;
  folio_id?: number | null;
  payment_mode_id: number;
  currency_id: number;
  amount: number | string;
  payment_date: string;
  reference?: string | null;
  status: string;
  received_by?: number | null;
};

type PaymentFormValues = {
  propertyId: number;
  reservationId?: number;
  folioId?: number;
  paymentModeId: number;
  currencyId: number;
  amount: number;
  paymentDate?: Dayjs;
  reference?: string;
  status: string;
  receivedBy?: number;
};

const statusLabels: Record<string, string> = {
  pending: "En attente",
  completed: "Termine",
  failed: "Echec",
  void: "Annule",
  refunded: "Rembourse",
};

const statusColors: Record<string, string> = {
  pending: "orange",
  completed: "green",
  failed: "red",
  void: "default",
  refunded: "cyan",
};

const PaymentsTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [paymentModes, setPaymentModes] = useState<PaymentMode[]>([]);
  const [payments, setPayments] = useState<HotelPayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [filters, setFilters] = useState({
    propertyId: "all" as number | "all",
    status: "all" as string | "all",
    reservationId: "" as string,
    folioId: "" as string,
  });
  const [editingPayment, setEditingPayment] = useState<HotelPayment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm<PaymentFormValues>();

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);
  const canCreate = Boolean(hasPermission?.("Hotellerie.Paiements.create"));
  const canEdit = Boolean(hasPermission?.("Hotellerie.Paiements.edit"));
  const canDelete = Boolean(hasPermission?.("Hotellerie.Paiements.delete"));
  const hasProperties = properties.length > 0;

  const currencyMap = useMemo(() => {
    const map = new Map<number, Currency>();
    currencies.forEach((currency) => map.set(currency.id, currency));
    return map;
  }, [currencies]);

  const paymentModeMap = useMemo(() => {
    const map = new Map<number, PaymentMode>();
    paymentModes.forEach((mode) => map.set(mode.id, mode));
    return map;
  }, [paymentModes]);

  const formatMoney = useCallback(
    (value: number | string, currencyId?: number | null) => {
      const numeric = Number(value || 0);
      const formatted = new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numeric);
      if (!currencyId) {
        return formatted;
      }
      const currency = currencyMap.get(currencyId);
      if (!currency) {
        return formatted;
      }
      return `${formatted} ${currency.symbol || currency.code}`;
    },
    [currencyMap]
  );

  const fetchProperties = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/properties`, {
        params,
      });
      if (response.data.success) {
        setProperties(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching properties:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des proprietes"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchCurrencies = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = { page: 1, limit: 200 };
      if (selectedCompany) {
        params.companyId = selectedCompany;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/currencies`, {
        params,
      });
      setCurrencies(response.data.currencies || []);
    } catch (error: any) {
      console.error("[HOTEL] Error fetching currencies:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des devises"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchPaymentModes = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany, limit: 2000 } : { limit: 2000 };
      const response = await axios.get(`${API_CONFIG.API_URL}/payment-modes`, {
        params,
      });
      const modes = response.data.paymentModes || [];
      setPaymentModes(modes);
    } catch (error: any) {
      console.error("[HOTEL] Error fetching payment modes:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des modes de paiement"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchPayments = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingPayments(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (filters.propertyId !== "all") {
        params.property_id = filters.propertyId;
      }
      if (filters.reservationId.trim()) {
        params.reservation_id = filters.reservationId.trim();
      }
      if (filters.folioId.trim()) {
        params.folio_id = filters.folioId.trim();
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/payments`, {
        params,
      });
      if (response.data.success) {
        setPayments(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching payments:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des paiements"
      );
    } finally {
      setLoadingPayments(false);
    }
  }, [filters.folioId, filters.propertyId, filters.reservationId, requiresCompanySelection, selectedCompany]);

  useEffect(() => {
    fetchProperties();
    fetchCurrencies();
    fetchPaymentModes();
  }, [fetchCurrencies, fetchPaymentModes, fetchProperties]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const filteredPayments = useMemo(() => {
    if (filters.status === "all") {
      return payments;
    }
    return payments.filter((payment) => payment.status === filters.status);
  }, [filters.status, payments]);

  const openCreateModal = () => {
    setEditingPayment(null);
    form.resetFields();
    form.setFieldsValue({
      status: "completed",
      amount: 0,
      paymentDate: dayjs(),
    });
    setIsModalOpen(true);
  };

  const openEditModal = (payment: HotelPayment) => {
    setEditingPayment(payment);
    form.setFieldsValue({
      propertyId: payment.property_id,
      reservationId: payment.reservation_id || undefined,
      folioId: payment.folio_id || undefined,
      paymentModeId: payment.payment_mode_id,
      currencyId: payment.currency_id,
      amount: Number(payment.amount || 0),
      paymentDate: payment.payment_date ? dayjs(payment.payment_date) : undefined,
      reference: payment.reference || undefined,
      status: payment.status,
      receivedBy: payment.received_by || undefined,
    });
    setIsModalOpen(true);
  };

  const handleSavePayment = async () => {
    const values = await form.validateFields();
    if (!editingPayment && !canCreate) {
      message.warning("Vous n'avez pas les droits pour creer un paiement.");
      return;
    }
    if (editingPayment && !canEdit) {
      message.warning("Vous n'avez pas les droits pour modifier ce paiement.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      reservation_id: values.reservationId || null,
      folio_id: values.folioId || null,
      payment_mode_id: values.paymentModeId,
      currency_id: values.currencyId,
      amount: values.amount ?? 0,
      payment_date: values.paymentDate
        ? values.paymentDate.format("YYYY-MM-DD HH:mm:ss")
        : null,
      reference: values.reference || null,
      status: values.status,
      received_by: values.receivedBy || null,
    };

    try {
      if (editingPayment) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/payments/${editingPayment.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Paiement mis a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/payments`,
          payload
        );
        if (response.data.success) {
          message.success("Paiement cree.");
        }
      }
      setIsModalOpen(false);
      setEditingPayment(null);
      fetchPayments();
    } catch (error: any) {
      console.error("[HOTEL] Error saving payment:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde du paiement"
      );
    }
  };

  const confirmDeletePayment = (payment: HotelPayment) => {
    if (!canDelete) {
      message.warning("Vous n'avez pas les droits pour supprimer ce paiement.");
      return;
    }
    Modal.confirm({
      title: "Supprimer le paiement",
      content: "Confirmer la suppression de ce paiement ?",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/payments/${payment.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Paiement supprime.");
            fetchPayments();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting payment:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression du paiement"
          );
        }
      },
    });
  };

  const columns: ColumnsType<HotelPayment> = [
    {
      title: "Paiement",
      key: "payment",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{formatMoney(record.amount, record.currency_id)}</Text>
          <Text type="secondary">
            {record.property_name || `Propriete #${record.property_id}`}
          </Text>
        </Space>
      ),
    },
    {
      title: "Mode",
      dataIndex: "payment_mode_id",
      key: "payment_mode_id",
      render: (value: number) =>
        paymentModeMap.get(value)?.name || `Mode #${value}`,
    },
    {
      title: "Date",
      dataIndex: "payment_date",
      key: "payment_date",
      render: (value: string) => dayjs(value).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (value: string) => (
        <Tag color={statusColors[value] || "default"}>
          {statusLabels[value] || value}
        </Tag>
      ),
    },
    {
      title: "Reservation",
      dataIndex: "reservation_id",
      key: "reservation_id",
      render: (value: number | null) => value || "-",
    },
    {
      title: "Folio",
      dataIndex: "folio_id",
      key: "folio_id",
      render: (value: number | null) => value || "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          {canEdit ? (
            <Button type="link" onClick={() => openEditModal(record)}>
              Modifier
            </Button>
          ) : null}
          {canDelete ? (
            <Button type="link" danger onClick={() => confirmDeletePayment(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder aux paiements."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Card
          title="Paiements"
          extra={
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchPayments}
                loading={loadingPayments}
              >
                Rafraichir
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
                disabled={!canCreate || !hasProperties}
              >
                Nouveau paiement
              </Button>
            </Space>
          }
        >
          {!hasProperties ? (
            <Alert
              message="Aucune propriete configuree"
              description="Creez une propriete dans l'onglet Configuration pour activer la creation de paiements."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : null}
          <Form layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <Form.Item label="Propriete">
                  <Select
                    value={filters.propertyId}
                    onChange={(value) =>
                      setFilters((prev) => ({ ...prev, propertyId: value }))
                    }
                    options={[
                      { value: "all", label: "Tous" },
                      ...properties.map((property) => ({
                        value: property.id,
                        label: property.name,
                      })),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Statut">
                  <Select
                    value={filters.status}
                    onChange={(value) =>
                      setFilters((prev) => ({ ...prev, status: value }))
                    }
                    options={[
                      { value: "all", label: "Tous" },
                      ...Object.keys(statusLabels).map((key) => ({
                        value: key,
                        label: statusLabels[key],
                      })),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="ID reservation">
                  <Input
                    placeholder="ID reservation"
                    value={filters.reservationId}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        reservationId: event.target.value,
                      }))
                    }
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="ID folio">
                  <Input
                    placeholder="ID folio"
                    value={filters.folioId}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, folioId: event.target.value,
                      }))
                    }
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Table
            columns={columns}
            dataSource={filteredPayments}
            rowKey="id"
            loading={loadingPayments}
            pagination={{ pageSize: 8 }}
          />
        </Card>
      </Space>

      <Modal
        title={editingPayment ? "Modifier paiement" : "Nouveau paiement"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingPayment(null);
        }}
        onOk={handleSavePayment}
        okText="Enregistrer"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select
              disabled={Boolean(editingPayment)}
              options={properties.map((property) => ({
                value: property.id,
                label: property.name,
              }))}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="ID reservation" name="reservationId">
                <InputNumber style={{ width: "100%" }} min={1} disabled={Boolean(editingPayment)} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="ID folio" name="folioId">
                <InputNumber style={{ width: "100%" }} min={1} disabled={Boolean(editingPayment)} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Mode de paiement"
                name="paymentModeId"
                rules={[{ required: true, message: "Mode de paiement requis" }]}
              >
                <Select
                  disabled={Boolean(editingPayment)}
                  options={paymentModes.map((mode) => ({
                    value: mode.id,
                    label: mode.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Devise"
                name="currencyId"
                rules={[{ required: true, message: "Devise requise" }]}
              >
                <Select
                  disabled={Boolean(editingPayment)}
                  options={currencies.map((currency) => ({
                    value: currency.id,
                    label: `${currency.code} - ${currency.name}`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Montant" name="amount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Date paiement" name="paymentDate">
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Statut" name="status">
                <Select
                  options={Object.keys(statusLabels).map((key) => ({
                    value: key,
                    label: statusLabels[key],
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Recu par" name="receivedBy">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Reference" name="reference">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PaymentsTab;
